// Builds the payload the customer survey renders when it's shown as an admin
// preview. Used by the Survey Setup page's live preview panel, which walks the
// whole survey from the settings currently in the editor.
//
// The shape mirrors what the public GET /v1/welcome-survey/survey endpoint
// returns for a real customer, so InfoVerificationPage needs no special casing.

// The stand-in customer shown on step 1. Deliberately obvious sample data — no
// real customer is ever loaded into a preview, and nothing is submitted.
export const SAMPLE_CUSTOMER = {
  name: "Sample Customer",
  phone: "5555555555",
  email: "sample.customer@example.com",
  address: "123 Sample Street",
  city: "Springfield",
  state: "UT",
  zip: "84000",
};

// Plan figures for the sample subscription. The real numbers come from
// FieldRoutes per customer, so a preview can only show representative ones.
export const SAMPLE_PLAN = {
  agreement_length: 12,
  initial_amount: 199,
  recurring_amount: 119,
  billing_frequency_label: "billed quarterly",
};

// Visit months used when a service type has no seasonal windows configured.
const DEFAULT_SERVICE_MONTHS = [3, 6, 9, 12];

// Inclusive month-window test with year-end wrap, matching the backend's
// monthInWindow so previews label months the way the live survey does.
function monthInWindow(month, start, end) {
  if (start <= end) return month >= start && month <= end;
  return month >= start || month <= end;
}

// Mirror of WelcomeSurveyServiceSeasonService.buildSchedule: label each visit
// month with the season window it falls in, plus that window's target pests.
export function buildSchedule(months, seasons) {
  return months.map(month => {
    const season = seasons.find(candidate =>
      monthInWindow(month, candidate.start_month, candidate.end_month)
    );
    return {
      month,
      season_name: season?.name ?? null,
      target_pests: season?.target_pests ?? [],
    };
  });
}

// Every month covered by a window, inclusive and wrapping across the year end
// (e.g. Nov–Feb -> [11, 12, 1, 2]). Empty for an incomplete/invalid window.
function monthsInWindow(start, end) {
  const valid = m => Number.isInteger(m) && m >= 1 && m <= 12;
  if (!valid(start) || !valid(end)) return [];
  const months = [];
  let month = start;
  // Capped at 12 iterations so a bad window can never loop forever.
  for (let i = 0; i < 12; i += 1) {
    months.push(month);
    if (month === end) break;
    month = month === 12 ? 1 : month + 1;
  }
  return months;
}

// The visit months to show for a service type: every month inside each
// configured window (so the survey labels the whole window with its pests, not
// just its first month), falling back to a quarterly cadence when none are set.
export function serviceMonthsFor(seasons) {
  if (!seasons.length) return DEFAULT_SERVICE_MONTHS;
  const months = new Set();
  seasons.forEach(season =>
    monthsInWindow(season.start_month, season.end_month).forEach(month =>
      months.add(month)
    )
  );
  return months.size
    ? [...months].sort((a, b) => a - b)
    : DEFAULT_SERVICE_MONTHS;
}

// Seasons for a service type out of the API's { serviceType: windows } map.
// Matched case-insensitively, the way the backend resolves a subscription's
// service type against the configured windows.
export function seasonsForServiceType(seasonsByService, serviceType) {
  if (!seasonsByService || !serviceType) return [];
  const entry = Object.entries(seasonsByService).find(
    ([name]) => name.toLowerCase() === serviceType.toLowerCase()
  );
  return Array.isArray(entry?.[1]) ? entry[1] : [];
}

// One preview payload for a single service type. `includedPests` /
// `notIncludedPests` are PestCoverageItem objects ({ name, image_url,
// fr_insect_id }) and `seasons` are ServiceSeasonView objects; both can be
// empty. Branding fields fall back to blank, which the survey renders without.
export function buildPreviewData({
  serviceType,
  includedPests = [],
  notIncludedPests = [],
  seasons = [],
  contractText = "",
  termsText = "",
  headerImageUrl = "",
  accentColor = "",
  cancellationFee = null,
}) {
  const months = serviceMonthsFor(seasons);

  return {
    ...SAMPLE_CUSTOMER,
    welcome_survey_contract_text: contractText ?? "",
    welcome_survey_terms_text: termsText ?? "",
    header_image_url: headerImageUrl ?? "",
    accent_color: accentColor ?? "",
    subscriptions: [
      {
        ...SAMPLE_PLAN,
        fr_subscription_id: null,
        service_type: serviceType || "Pest Control",
        cancellation_fee: cancellationFee,
        season_start_month: null,
        season_end_month: null,
        estimated_visits: months.length,
        visits_per_year: months.length,
        service_months: months,
        service_schedule: buildSchedule(months, seasons),
        included_pests: includedPests,
        not_included_pests: notIncludedPests,
      },
    ],
  };
}
